import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import {
  Box,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Chip,
  Autocomplete,
  TextField,
} from '@mui/material';
import { ElementType, useState, useEffect, useContext } from 'react';

import {
  MetricsTimeframe,
  MetricsTimeframeOption,
} from '@features/enterpriseMetrics';
import { useGetMetrics } from '@features/enterpriseMetrics/hooks/useGetSavedMessages';
import {
  lightGrey,
  white,
  darkBlue,
  interviewBlue,
  primaryBlue,
  primaryPurple,
} from '@styles/colors';
import { bold } from '@styles/fontWeights';
import { authContext } from '@staffedupusa/shared-components';
import { useGetApplicationSources } from '@features/employerMetrics/hooks/useGetApplicationSources';
import { ApplicationSource } from '@features/employerMetrics';
import { PieChart } from '@mui/x-charts/PieChart';
import { mapPlanStatusAndColor } from '@utils/companyHelper';

type Metric = {
  value: string;
  title: string;
  Icon: ElementType;
  iconColor: string;
};

const EnterpriseMetricsHeader = () => {
  const { authData } = useContext(authContext);
  const [selectedTimePeriod, setSelectedTimePeriod] =
    useState<MetricsTimeframe>('7D');
  const [expanded, setExpanded] = useState<boolean>(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | number>(
    'ALL',
  );
  const { applicationSources, isApplicationSourcesLoading } =
    useGetApplicationSources({
      selectedDatePeriod: selectedTimePeriod,
      selectedCompanyId,
    });

  const { metricsData, isLoadingMetrics } = useGetMetrics({
    // @ts-ignore
    enterprise_id: authData?.user?.enterprise?.id,
    selectedCompanyId,
  });
  const [companiesData, setCompaniesData] = useState<any[]>([]);
  const companyCount = metricsData?.company_count || 1;

  const getApplicationSourceData = (
    applicationSources:
      | { status: number; message: string; objects: ApplicationSource[] }
      | undefined,
    sourceId: number,
  ) => {
    return (
      applicationSources?.objects?.find(
        (item: { application_source: number }) =>
          item.application_source === sourceId,
      )?.count || 0
    );
  };

  useEffect(() => {
    if (metricsData?.companies) {
      const updatedCompaniesData = mapPlanStatusAndColor(metricsData.companies);
      setCompaniesData(updatedCompaniesData);
    }
  }, [metricsData]);

  const timeframeOptions: MetricsTimeframeOption[] = [
    {
      value: '7D',
      title: '7 days',
    },
    {
      value: '30D',
      title: '30 days',
    },
    {
      value: '1Y',
      title: '1 year',
    },
    {
      value: 'ALL',
      title: 'All time',
    },
  ];

  const pageViews =
    metricsData?.metrics?.total_page_views[selectedTimePeriod] || 0;
  const applications =
    metricsData?.metrics?.total_applications[selectedTimePeriod] || 0;

  const adjustedPageViews =
    applications > pageViews
      ? Number(applications) + Number(pageViews)
      : pageViews;

  const metrics: Metric[] = [
    // {
    //   value:
    //     selectedCompanyId === 'ALL'
    //       ? `${((metricsData?.metrics?.engagement_rate[selectedTimePeriod] || 0) / companyCount).toFixed(1)}%`
    //       : `${metricsData?.metrics?.engagement_rate[selectedTimePeriod] || 0}%`,
    //   title: 'Engagement',
    //   Icon: ChatIcon,
    //   iconColor: 'success.main',
    // },

    {
      value: String(adjustedPageViews),
      title: 'Page Views',
      Icon: RemoveRedEyeOutlinedIcon,
      iconColor: 'primary.main',
    },
    {
      value: String(
        metricsData?.metrics?.total_applications[selectedTimePeriod] || 0,
      ),
      title: 'Applications',
      Icon: SendOutlinedIcon,
      iconColor: 'yellow.main',
    },
    {
      value:
        selectedCompanyId === 'ALL'
          ? String(
              (
                (metricsData?.metrics?.average_response_time[
                  selectedTimePeriod
                ] || 0) / companyCount
              ).toFixed(0) + ' days',
            )
          : String(
              (metricsData?.metrics?.average_response_time[
                selectedTimePeriod
              ] || 0) + ' days',
            ),
      title: 'Response time',
      Icon: AccessTimeOutlinedIcon,
      iconColor: 'lightPurple.main',
    },
    // {
    //   value:
    //     selectedCompanyId === 'ALL'
    //       ? `${Number(metricsData?.metrics?.application_conversion_rate[selectedTimePeriod] || 0).toFixed(1)}%`
    //       : `${Number(metricsData?.metrics?.application_conversion_rate[selectedTimePeriod])?.toFixed(2) || '0.00'}%`,
    //   title: 'Conversion rate',
    //   Icon: AutorenewOutlinedIcon,
    //   iconColor: 'purple.main',
    // },
  ];

  const applicationSourcesPieData = [
    {
      id: 0,
      value: getApplicationSourceData(applicationSources, 0),
      label: 'StaffedUp',
      color: primaryBlue,
    },
    {
      id: 2,
      value: getApplicationSourceData(applicationSources, 2),
      label: 'Social Media',
      color: interviewBlue,
    },
    {
      id: 1,
      value: getApplicationSourceData(applicationSources, 1),
      label: 'Employer Website',
      color: darkBlue,
    },

    {
      id: 3,
      value: getApplicationSourceData(applicationSources, 3),
      label: 'External Job Boards',
      color: primaryPurple,
    },
  ];
  return (
    <Accordion
      elevation={0}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      sx={{
        maxHeight: !expanded ? '40px' : 'auto',
        backgroundColor: 'transparent',
        '& .Mui-expanded': {
          minHeight: 0,
          margin: 0,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="metrics-content"
        id="metrics-header"
        sx={{
          pl: 2,
          pr: 2,
          backgroundColor: white,
          borderRadius: expanded ? '8px 8px 0 0' : '8px',
          '& .MuiAccordionSummary-content': {
            margin: '0 !important',
          },
        }}
      >
        <Typography sx={{ fontSize: '16px', fontWeight: 'bold', padding: 1 }}>
          Hiring Metrics
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Grid
          container
          bgcolor={white}
          borderRadius="0 0 8px 8px"
          width="100%"
          height="100%"
          columnSpacing={2}
          rowGap={2}
          sx={{ ml: 0, padding: '0 20px 20px 20px' }}
        >
          <Grid
            container
            bgcolor={white}
            p={4}
            width="100%"
            height="100%"
            columnSpacing={2}
            rowGap={2}
            sx={{ ml: 0 }}
          >
            <Grid
              container
              spacing={3}
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                paddingTop: {
                  sm: 'auto',
                  md: '0 !important',
                },
              }}
            >
              <Grid item xs={12} lg={6} xl={5}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems="center"
                  justifyContent="center"
                  spacing={3}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      width: { xs: '100%', sm: '50%' },
                    }}
                  >
                    {applicationSourcesPieData
                      .filter((item) => item.value > 0)
                      .map((item) => (
                        <Box
                          key={item.id}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Chip
                            sx={{
                              backgroundColor: item.color,
                              width: 16,
                              height: 16,
                            }}
                            label=""
                          />
                          <Typography variant="body2">{item.label}</Typography>
                        </Box>
                      ))}
                  </Box>
                  <Box
                    sx={{
                      width: { xs: '100%', sm: '50%' },
                      display: 'flex',
                      '&.MuiBox-root > div': {
                        overflow: 'visible !important',
                        marginLeft: '10px',
                        '&.MuiBox-root > div > svg': {
                          overflow: 'visible !important',
                        },
                      },
                    }}
                  >
                    {isApplicationSourcesLoading ? (
                      <CircularProgress
                        sx={{
                          color: primaryBlue,
                        }}
                      />
                    ) : (
                      applicationSourcesPieData.some(
                        (item) => item.value > 0,
                      ) && (
                        <PieChart
                          colors={applicationSourcesPieData.map(
                            (item) => item.color,
                          )}
                          series={[
                            {
                              innerRadius: 40,
                              outerRadius: 80,
                              data: applicationSourcesPieData.map((item) => ({
                                id: item.id,
                                value: item.value,
                              })),
                            },
                          ]}
                          width={200}
                          height={200}
                          sx={{ overflow: 'visible !important' }}
                        />
                      )
                    )}
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} lg={6} xl={7}>
                <Grid container spacing={3}>
                  <Grid
                    item
                    xs={6}
                    sm={12}
                    sx={{
                      alignSelf: 'center',
                      gap: '10px',
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      paddingTop: '0 !important',
                    }}
                  >
                    <FormControl
                      sx={{ width: { xs: '100%', sm: '50%' } }}
                      size="small"
                      variant="outlined"
                      fullWidth
                    >
                      <Autocomplete
                        size="small"
                        options={[{ company_id: 'ALL', name: 'All Companies' }, ...companiesData]}
                        getOptionLabel={(option) =>
                          option.company_id === 'ALL'
                            ? 'All Companies'
                            : `${option.name}, ${option?.s_city}, ${option?.s_state}`
                        }
                        value={
                          [{ company_id: 'ALL', name: 'All Companies' }, ...companiesData].find(
                            (c) => c.company_id === selectedCompanyId
                          ) || null
                        }
                        onChange={(_, newValue) => {
                          setSelectedCompanyId(newValue?.company_id || 'ALL');
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option.company_id === value?.company_id
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Company"
                            size="small"
                            style={{ fontSize: '14px' }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.company_id}>
                            {option.company_id === 'ALL'
                              ? 'All Companies'
                              : `${option.name}, ${option?.s_city}, ${option?.s_state}`}
                            {option.company_id !== 'ALL' && (
                              <span
                                style={{
                                  color: '#fff',
                                  fontSize: '9px',
                                  fontWeight: 600,
                                  backgroundColor: option.color,
                                  marginLeft: '10px',
                                  padding: '1px 5px 2px',
                                  borderRadius: 5,
                                }}
                              >
                                {option.plan_status}
                              </span>
                            )}
                          </li>
                        )}
                      />
                    </FormControl>
                    <FormControl
                      sx={{ width: { sx: '100%', sm: '50%' } }}
                      size="small"
                      variant="outlined"
                      fullWidth
                    >
                      <Select
                        size="small"
                        value={selectedTimePeriod}
                        onChange={(e) =>
                          setSelectedTimePeriod(
                            e.target.value as MetricsTimeframe,
                          )
                        }
                        sx={{ fontSize: '14px' }}
                        MenuProps={{
                          PaperProps: { style: { borderRadius: 5 } },
                        }}
                      >
                        {timeframeOptions.map(({ value, title }) => (
                          <MenuItem key={value} value={value}>
                            <span
                              style={{
                                borderRadius: 5,
                              }}
                            >
                              {title}
                            </span>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {metrics.map(({ title, Icon, value, iconColor }) => (
                    <Grid item xs={6} md={4} key={title}>
                      <Box
                        bgcolor={lightGrey}
                        p={4}
                        borderRadius={2}
                        textAlign="center"
                        height="100%"
                        sx={{ borderRadius: 8 }}
                      >
                        <Typography
                          fontWeight={bold}
                          variant="h3"
                          component="p"
                        >
                          {Icon && !isLoadingMetrics ? (
                            <>
                              <Icon
                                sx={{
                                  verticalAlign: 'middle',
                                  mr: 1,
                                  color: iconColor,
                                }}
                              />
                              {value || 0}
                            </>
                          ) : (
                            <CircularProgress size={20} />
                          )}
                        </Typography>
                        <Typography variant="h6" style={{ fontSize: '14px' }}>
                          {title}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default EnterpriseMetricsHeader;
